import { createFileRoute } from '@tanstack/react-router';

import { OutreachFeedPage } from '@apps/web/components/home/outreach_feed_page';
import { PostSetupOnboardingPage } from '@apps/web/components/home/post_setup_onboarding_page';
import { PreOnboardingChecklistPage } from '@apps/web/components/home/pre_onboarding_checklist_page';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    isPreOnboardingOrganization,
    isProvisioningOrganization,
} from '@apps/web/lib/customer_lifecycle';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

type HomeSearch = {
    readonly view?: 'checklist';
    readonly automation_id?: string;
};

export const Route = createFileRoute('/_app/(main)/home')({
    validateSearch: (search: Record<string, unknown>): HomeSearch => ({
        ...(search['view'] === 'checklist' ? { view: 'checklist' } : {}),
        ...(typeof search['automation_id'] === 'string'
            ? { automation_id: search['automation_id'] }
            : {}),
    }),
    head: () => ({
        meta: [
            {
                title: 'Home | Phoebe',
            },
        ],
    }),
    component: HomeRoute,
});

function HomeRoute() {
    const organizationId = useOrganizationId();
    const { view } = Route.useSearch();
    const navigate = Route.useNavigate();
    const {
        data: organization,
        error: organizationError,
        isLoading: isLoadingOrganization,
    } = useOrganization(organizationId, {
        reason: 'home',
    });
    if (
        organizationId &&
        (isLoadingOrganization || organizationError || !organization)
    ) {
        return null;
    }

    if (organizationId && isPreOnboardingOrganization(organization)) {
        return <PreOnboardingChecklistPage organizationId={organizationId} />;
    }

    if (organizationId && isProvisioningOrganization(organization)) {
        // The checklist stays reachable at /home?view=checklist after the
        // wizard takes over the default Home view, but read-only: edits
        // during provisioning would re-fire provisioning side effects, so
        // customers review what they submitted and contact CX for changes.
        if (view === 'checklist') {
            return (
                <PreOnboardingChecklistPage
                    organizationId={organizationId}
                    readOnly={true}
                />
            );
        }
        return (
            <PostSetupOnboardingPage
                organizationId={organizationId}
                onOpenChecklist={() =>
                    void navigate({ search: { view: 'checklist' } })
                }
            />
        );
    }

    if (organizationId) {
        return <OutreachFeedPage showActivityCard={true} />;
    }

    return <OutreachFeedPage />;
}
